import {
    getOrderLineModifiers,
    getVariationItem,
    isComp,
} from 'components/Sell/utils/common'
import { isOpenItemId } from 'constant/magicNumber'
import Decimal from 'decimal.js'
import { keys, values } from 'lodash-es'
import {
    TaxContract,
    VoidCompReasonModel,
    OrderLineModifierProcessContract,
} from 'models'
import {
    OrderItemViewModel,
    ModifierOptionLookup,
    ItemMap,
    IServiceCharge,
    DiscountContractWithCustom,
} from 'store/models/sellModels'
import { sumDecimal } from 'utilities/decimalsManipulate'
import { isCompItem } from '../orderItemUtil'
import calculateLine from '../processLine'
import { LineModelExtend } from './models/IOrderCalculationModel'
import calculateOrder from './orderCalculation'
import { round } from './common'

export function linesAndOrderCalculation(
    orderItems: OrderItemViewModel[],
    isCalculateCompAfterDiscount: boolean,
    taxes: TaxContract[],
    modifierOptionMap: ModifierOptionLookup,
    itemsMap: ItemMap,
    serviceCharge: IServiceCharge,
    entireOrderDiscounts: DiscountContractWithCustom[],
    entireOrderVoidComp: VoidCompReasonModel | undefined,
) {
    const processedOrderItems = orderItems.map((l) =>
        calculateLine(l, isCalculateCompAfterDiscount),
    )
    const lineWithTaxes = prepareLineForCalculation(
        processedOrderItems,
        taxes,
        modifierOptionMap,
        itemsMap,
    )

    const calculatedOrder = calculateOrder(
        lineWithTaxes,
        serviceCharge,
        entireOrderDiscounts,
        isComp(entireOrderVoidComp) ? entireOrderVoidComp : undefined,
        isCalculateCompAfterDiscount,
    )
    return { processedOrderItems, calculatedOrder }
}

/**
 * add tax to line and prepare combo and modifier lines
 * @param calculatedLines
 * @param taxes
 * @param modifierOptionMap
 * @returns LineModelExtend[]
 */
function prepareLineForCalculation(
    calculatedLines: OrderItemViewModel[],
    taxes: TaxContract[],
    modifierOptionMap: ModifierOptionLookup,
    itemsMap: ItemMap,
): LineModelExtend[] {
    return calculatedLines.map<LineModelExtend>((l, index) => {
        let { itemId, tax, isTaxInclusive } = l.item

        // in case of Open Item, tax and isTaxInclusive can be overridden freely
        if (isOpenItemId(itemId)) {
            isTaxInclusive = l.overriddenIsTaxInclusive ?? false
            tax = taxes.find((t) => t.taxID === l.overriddentaxID)
        }

        const lineWithTax: LineModelExtend = {
            itemName: l.item.name,
            orderItemId: l.id,
            tax,
            isTaxInclusive,
            amount: l.isItemUnavailable ? 0 : l.amount,
            lineAmount: l.isItemUnavailable ? 0 : l.amount,
            lineTotal: l.lineTotal,
            lineTotalWithItemDiscount: l.lineTotalWithItemDiscount ?? 0,
            itemDiscountAmount: l.itemDiscountAmount ?? 0,
            lineCompDiscount: l.lineCompDiscount ?? 0,
            isComp: isCompItem(l),
            voidComp: l.voidComp,
            discounts: l.discounts ?? [],
            comboItems: [],
            modifierLines: [],
            subtotal: l.subtotal,
            orderDiscountAmount: new Decimal(0),
            taxAmount: new Decimal(0),
            sortingIndex: index,
            excludeFromItemTaxAmount: new Decimal(0),
            isDepositAndCreditItem: Boolean(l.item.baseDepositAndCreditItemId),
        }
        lineWithTax.modifierLines = getModifierLines(
            getOrderLineModifiers(l.usedModifiers),
            lineWithTax,
            taxes,
            modifierOptionMap,
        )

        lineWithTax.comboItems = getComboLines(
            l,
            lineWithTax,
            itemsMap,
            taxes,
            modifierOptionMap,
        )
        return lineWithTax
    })
}

function getComboLines(
    orderItem: OrderItemViewModel,
    parentLine: LineModelExtend,
    itemsMap: ItemMap,
    taxes: TaxContract[],
    modifierOptionMap: ModifierOptionLookup,
): LineModelExtend[] {
    return keys(orderItem.usedItems).map<LineModelExtend>((path, i) => {
        const comboItem = orderItem.usedItems[path]
        const item = itemsMap[comboItem.nodeItemId]
        const cLine: LineModelExtend = {
            itemName: item.name,
            orderItemId: `${orderItem.id}_${comboItem.nodeId}_${path}`,
            amount: comboItem.quantity,
            lineAmount: comboItem.isFixedItem ? 1 : comboItem.quantity,
            sortingIndex: i,
            isComp: parentLine.isComp,
            lineTotal: 0,
            voidComp: parentLine.voidComp,
            subtotal: new Decimal(0),
            comboItems: [],
            isTaxInclusive: false,
            tax: undefined,
            modifierLines: [],
            orderDiscountAmount: new Decimal(0),
            taxAmount: new Decimal(0),
            excludeFromItemTaxAmount: new Decimal(0),
            lineTotalWithItemDiscount: 0,
            //combo item always not deposite item
            isDepositAndCreditItem: false,
        }

        const itemVariantOptions = values(comboItem.usedVariants)
        const variantItem = getVariationItem(item, itemVariantOptions)

        cLine.isTaxInclusive = variantItem.isTaxInclusive
        cLine.tax = variantItem.tax
        cLine.modifierLines = getModifierLines(
            getOrderLineModifiers(comboItem.usedModifiers),
            cLine,
            taxes,
            modifierOptionMap,
        )
        cLine.lineTotal = round(
            sumDecimal(cLine.modifierLines.map((a) => a.subtotal)).plus(
                comboItem.addPrice,
            ),
        ).toNumber()
        cLine.subtotal = new Decimal(cLine.lineTotal).mul(cLine.lineAmount)

        if (cLine.subtotal.lessThanOrEqualTo(0)) {
            cLine.tax = undefined
        }
        return cLine
    })
}

function getModifierLines(
    modifierLines: OrderLineModifierProcessContract[],
    parentLine: LineModelExtend,
    taxes: TaxContract[],
    modifierOptionMap: ModifierOptionLookup,
): LineModelExtend[] {
    return modifierLines.flatMap<LineModelExtend>((mod) => {
        const option = modifierOptionMap[mod.modifierOptionID!]
        const price = mod.priceAdd ?? 0
        const tax =
            price <= 0 ? undefined : taxes.find((t) => t.taxID === option.taxID)
        const modLine: LineModelExtend = {
            ...parentLine,
            itemName: option.displayName ?? option.itemName!,
            orderItemId: `${parentLine.orderItemId}_${mod.modifierOptionID}`,
            discounts: [],
            lineCompDiscount: 0,
            itemDiscountAmount: 0,
            tax,
            isTaxInclusive: option.isTaxInclusive ?? false,
            amount: mod.quantity ?? 1,
            lineAmount: mod.quantity ?? 1,
            lineTotal: mod.priceAdd ?? 0,
            lineTotalWithItemDiscount: 0,
            subtotal: new Decimal(mod.priceAdd ?? 0).mul(mod.quantity ?? 1),
        }

        return [modLine].concat(
            mod.subModifiers?.map<LineModelExtend>((sub) => {
                const subMod = modifierOptionMap[sub.modifierOptionID!]
                const subPrice = sub.priceAdd ?? 0
                const tax =
                    subPrice <= 0
                        ? undefined
                        : taxes.find((t) => t.taxID === subMod.taxID)

                return {
                    ...modLine,
                    itemName: subMod.displayName ?? subMod.itemName!,
                    orderItemId: `${modLine.orderItemId}_${subMod.modifierOptionID}`,
                    tax,
                    isTaxInclusive: subMod.isTaxInclusive ?? false,
                    amount: sub.quantity ?? 1,
                    lineAmount: sub.quantity ?? 1,
                    lineTotal: subPrice,
                    subtotal: new Decimal(subPrice)
                        .mul(sub.quantity ?? 1)
                        .mul(mod.quantity ?? 1),
                }
            }) ?? [],
        )
    })
}
